/** Проверка на ЕГН/ЛНЧ/БУЛСТАТ както в бекенда (ClientIdentityValidator, BulstatValidator). */

export const digitsOnly = (value?: string | null) => (value ?? '').replace(/\D/g, '');

const weighted = (digits: string, weights: number[]) =>
    weights.reduce((sum, weight, index) => sum + Number(digits[index]) * weight, 0);

export const isValidEgn = (value?: string | null): boolean => {
    const egn = digitsOnly(value);
    if (!/^\d{10}$/.test(egn)) return false;

    let year = Number(egn.slice(0, 2));
    let month = Number(egn.slice(2, 4));
    const day = Number(egn.slice(4, 6));
    if (month >= 1 && month <= 12) {
        year += 1900;
    } else if (month >= 21 && month <= 32) {
        year += 1800;
        month -= 20;
    } else if (month >= 41 && month <= 52) {
        year += 2000;
        month -= 40;
    } else {
        return false;
    }
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return false;

    const checksum = weighted(egn, [2, 4, 8, 5, 10, 9, 7, 3, 6]) % 11;
    return (checksum === 10 ? 0 : checksum) === Number(egn[9]);
};

/** ЛНЧ на чужденец: тегла 21,19,17,13,11,9,7,3,1, модул 10. */
export const isValidLnch = (value?: string | null): boolean => {
    const lnch = digitsOnly(value);
    return /^\d{10}$/.test(lnch) && weighted(lnch, [21, 19, 17, 13, 11, 9, 7, 3, 1]) % 10 === Number(lnch[9]);
};

const bulstatChecksum = (digits: string, primary: number[], fallback: number[]) => {
    const first = weighted(digits, primary) % 11;
    if (first !== 10) return first;
    const second = weighted(digits, fallback) % 11;
    return second === 10 ? 0 : second;
};

export const isValidBulstat = (value?: string | null): boolean => {
    const digits = digitsOnly(value);
    if (digits.length !== 9 && digits.length !== 13) return false;
    if (bulstatChecksum(digits, [1, 2, 3, 4, 5, 6, 7, 8], [3, 4, 5, 6, 7, 8, 9, 10]) !== Number(digits[8])) return false;
    if (digits.length === 9) return true;
    return bulstatChecksum(digits.slice(8, 12), [2, 7, 3, 5], [4, 9, 5, 7]) === Number(digits[12]);
};

/**
 * Идентификатор, с който физическо лице може да е регистрирано по ДДС: ЕГН, ЛНЧ или 9-цифрен БУЛСТАТ
 * (13 цифри има само поделение на юридическо лице).
 * Деветките и нулите са заместители за "няма ЕГН" (9999999999 минава контролната сума на ЛНЧ).
 */
export const isValidPersonIdentifier = (value?: string | null): boolean => {
    const digits = digitsOnly(value);
    if (!digits || /^(0+|9+)$/.test(digits)) return false;
    if (digits.length === 10) return isValidEgn(digits) || isValidLnch(digits);
    return digits.length === 9 && isValidBulstat(digits);
};
